import { PropsWithChildren, ReactNode } from 'react';
import { Row, TableOptions, useTable } from 'react-table';

import TableHeader from './TableHeader';
import TableBody from './TableBody';
import TableHeaderFilters from './TableHeaderFilters';
import styles from './Table.module.css';
import messages from './Table.messages';
import { TableFiltersOptions } from './Table.types';

type TableProps<D extends object> = PropsWithChildren<
  Pick<TableOptions<D>, 'columns' | 'data'> & {
    onRowClick?: (row: Row<D>) => void;
  }
> & {
  headerFilters?: TableFiltersOptions;
  customBody?: ReactNode;
  shouldShowCustomBody?: boolean;
  noFilterResults?: boolean;
};

export default function Table<D extends object>({
  columns,
  data,
  children,
  onRowClick,
  headerFilters,
  customBody,
  shouldShowCustomBody = false,
  noFilterResults = false,
}: TableProps<D>) {
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable({
    columns,
    data,
  });

  return (
    <div className={styles.tableWrapper}>
      <TableHeaderFilters {...headerFilters} />
      {shouldShowCustomBody ? (
        <>{customBody}</>
      ) : (
        <table className={styles.table} {...getTableProps()}>
          <TableHeader headerGroups={headerGroups} />
          {noFilterResults && (
            <tbody>
              <tr>
                <td colSpan={columns.length} className={styles.placeholder}>
                  {messages.noMatchesFound}
                </td>
              </tr>
            </tbody>
          )}
          <TableBody
            getTableBodyProps={getTableBodyProps}
            rows={rows}
            prepareRow={prepareRow}
            onRowClick={onRowClick}
          />
          {children}
        </table>
      )}
    </div>
  );
}
